import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactForm = ({ onSubmit }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, watch, getValues } = useForm({
    defaultValues: {
      enquiryType: '',
      marketingConsent: false,
      termsAccepted: false
    }
  });

  const termsAccepted = watch('termsAccepted');

  const enquiryTypes = [
    { value: 'bulk-purchase', label: 'Bulk Purchase (50+ bottles)' },
    { value: 'retail-inquiry', label: 'Retail Inquiry (Individual Purchase)' },
    { value: 'corporate-partnership', label: 'Corporate Partnership' },
    { value: 'distributorship', label: 'Distributorship Opportunity' },
    { value: 'product-information', label: 'Product Information' },
    { value: 'general-inquiry', label: 'General Inquiry' }
  ];

  const onFormSubmit = async (data) => {
    setIsSubmitting(true);
    
    const formData = {
      fullName: `${data?.firstName} ${data?.lastName}`,
      firstName: data?.firstName,
      lastName: data?.lastName,
      phoneNumber: data?.phone,
      city: data?.city,
      typeOfEnquiry: data?.enquiryType,
      email: data?.email,
      additionalDetails: data?.requirements,
      timestamp: new Date()?.toISOString(),
      formVersion: 'v2.0',
      source: 'contact_form'
    };

    try {
      await onSubmit?.(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <h3 className="text-xl font-poppins font-semibold text-text-primary mb-4">
        Personal Information
      </h3>
      
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Full Name *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            {...register('firstName', { 
              required: 'First name is required',
              minLength: { value: 2, message: 'First name must be at least 2 characters' }
            })}
            placeholder="First name"
            error={errors?.firstName?.message}
          />
          
          <Input
            {...register('lastName', { 
              required: 'Last name is required',
              minLength: { value: 2, message: 'Last name must be at least 2 characters' }
            })}
            placeholder="Last name"
            error={errors?.lastName?.message}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Phone Number *
        </label>
        <Input
          type="tel"
          {...register('phone', { 
            required: 'Phone number is required',
            pattern: {
              value: /^[+]?[\d\s\-()]{10,}$/,
              message: 'Please enter a valid phone number'
            }
          })}
          placeholder="+91 98765 43210"
          error={errors?.phone?.message}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          City *
        </label>
        <Input
          {...register('city', { 
            required: 'City is required',
            minLength: { value: 2, message: 'City must be at least 2 characters' }
          })}
          placeholder="Enter your city"
          error={errors?.city?.message}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Type of Enquiry *
        </label>
        <Select
          {...register('enquiryType', { required: 'Please select enquiry type' })}
          options={enquiryTypes}
          placeholder="Select type of enquiry"
          error={errors?.enquiryType?.message}
        />
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => setStep(2)}
          className="px-8"
          disabled={!getValues('firstName') || !getValues('lastName') || !getValues('phone') || !getValues('city') || !getValues('enquiryType')}
        >
          Next Step
          <Icon name="ArrowRight" size={16} />
        </Button>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <h3 className="text-xl font-poppins font-semibold text-text-primary mb-4">
        Additional Information
      </h3>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Email Address (Optional)
        </label>
        <Input
          type="email"
          {...register('email', { 
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address'
            }
          })}
          placeholder="Enter your email address"
          error={errors?.email?.message}
        />
        <p className="text-xs text-text-secondary mt-1">
          We'll use this for follow-up communications and updates
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Additional Requirements or Questions
        </label>
        <textarea
          {...register('requirements')}
          rows={4}
          className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary brand-transition resize-none"
          placeholder="Tell us about any specific requirements, questions about our products, or additional information you'd like to share..."
        />
      </div>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(1)}
        >
          <Icon name="ArrowLeft" size={16} />
          Previous
        </Button>
        
        <Button
          onClick={() => setStep(3)}
          className="px-8"
        >
          Review & Submit
          <Icon name="ArrowRight" size={16} />
        </Button>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <h3 className="text-xl font-poppins font-semibold text-text-primary mb-4">
        Review & Submit
      </h3>

      <div className="bg-surface rounded-xl p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-text-secondary">Full Name:</span>
            <span className="ml-2 font-medium text-text-primary">
              {getValues('firstName')} {getValues('lastName')}
            </span>
          </div>
          <div>
            <span className="text-text-secondary">Phone Number:</span>
            <span className="ml-2 font-medium text-text-primary">
              {getValues('phone')}
            </span>
          </div>
          <div>
            <span className="text-text-secondary">City:</span>
            <span className="ml-2 font-medium text-text-primary">
              {getValues('city')}
            </span>
          </div>
          <div>
            <span className="text-text-secondary">Type of Enquiry:</span>
            <span className="ml-2 font-medium text-text-primary">
              {enquiryTypes?.find(t => t?.value === getValues('enquiryType'))?.label}
            </span>
          </div>
          {getValues('email') && (
            <div className="md:col-span-2">
              <span className="text-text-secondary">Email:</span>
              <span className="ml-2 font-medium text-text-primary">
                {getValues('email')}
              </span>
            </div>
          )}
        </div>

        {getValues('requirements') && (
          <div>
            <span className="text-text-secondary text-sm">Additional Requirements:</span>
            <p className="mt-2 text-sm text-text-primary bg-background p-3 rounded-lg">
              {getValues('requirements')}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <Checkbox
          {...register('marketingConsent')}
          label="I agree to receive marketing communications from Mava Cold Beverages"
        />

        <Checkbox
          {...register('termsAccepted', { required: 'You must accept the terms and conditions' })}
          label="I agree to the Terms & Conditions and Privacy Policy *"
          error={errors?.termsAccepted?.message}
        />
      </div>

      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(2)}
        >
          <Icon name="ArrowLeft" size={16} />
          Previous
        </Button>
        
        <Button
          onClick={handleSubmit(onFormSubmit)}
          disabled={!termsAccepted || isSubmitting}
          loading={isSubmitting}
          className="px-8"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
          <Icon name="Send" size={16} />
        </Button>
      </div>
    </motion.div>
  );

  return (
    <div className="contact-form bg-background rounded-2xl p-8 liquid-shadow">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3]?.map((stepNumber) => (
            <div
              key={stepNumber}
              className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold text-sm brand-transition ${
                step >= stepNumber
                  ? 'bg-primary text-white' :'bg-muted text-text-secondary'
              }`}
            >
              {step > stepNumber ? (
                <Icon name="Check" size={16} color="white" />
              ) : (
                stepNumber
              )}
            </div>
          ))}
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-gradient-to-r from-primary to-accent h-2 rounded-full brand-transition"
            style={{ width: `${(step / 3) * 100}%` }}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </form>
    </div>
  );
};

export default ContactForm;